import type { CSSProperties } from "react";
import type { Peg } from "./engine.js";

/**
 * Cipher Crack — combination tumblers and feedback pegs.
 *
 * Visuals ported from the Claude Design handoff (`Safe.jsx`), shrunk one
 * size from `lg` (84×108, 60px font) to `md` (60×80, 40px font) so the full
 * page fits a 1280×720 viewport without scroll.
 */

// ---- CombinationSlot ----------------------------------------------------
// Single tumbler dial showing a digit, with state styling.

export type SlotState =
  | "empty"
  | "active"
  | "filled"
  | "revealed-correct"
  | "revealed-wrong";

export type SlotSize = "sm" | "md" | "lg";

interface SlotDims {
  w: number;
  h: number;
  f: number;
  sep: number;
}

const SLOT_DIMS: Record<SlotSize, SlotDims> = {
  sm: { w: 44, h: 60, f: 30, sep: 6 },
  md: { w: 60, h: 80, f: 40, sep: 7 },
  lg: { w: 84, h: 108, f: 60, sep: 8 },
};

interface SlotStyleSet {
  rim: string;
  glow: string;
  faceTop: string;
  faceBottom: string;
  digitColor: string;
  digitGlow: string;
}

function styleForState(state: SlotState): SlotStyleSet {
  switch (state) {
    case "active":
      return {
        rim: "var(--gold-bright)",
        glow:
          "0 0 18px rgba(241,203,107,0.45), inset 0 0 14px rgba(241,203,107,0.18)",
        faceTop: "#181D29",
        faceBottom: "#0B0E16",
        digitColor: "var(--gold-bright)",
        digitGlow: "0 0 12px var(--gold)",
      };
    case "filled":
      return {
        rim: "var(--gold)",
        glow:
          "0 0 12px rgba(196,154,70,0.35), inset 0 1px 0 rgba(255,232,168,0.06)",
        faceTop: "#141925",
        faceBottom: "#080B12",
        digitColor: "var(--gold-bright)",
        digitGlow: "0 0 8px var(--gold)",
      };
    case "revealed-correct":
      return {
        rim: "var(--gold-bright)",
        glow:
          "0 0 24px rgba(241,203,107,0.7), inset 0 0 22px rgba(241,203,107,0.28)",
        faceTop: "#231C0E",
        faceBottom: "#120D04",
        digitColor: "#FFE8A8",
        digitGlow: "0 0 16px var(--gold-bright), 0 0 32px var(--gold)",
      };
    case "revealed-wrong":
      return {
        rim: "rgba(224,69,69,0.65)",
        glow:
          "0 0 18px rgba(224,69,69,0.4), inset 0 0 16px rgba(224,69,69,0.15)",
        faceTop: "#1E1216",
        faceBottom: "#0A0608",
        digitColor: "var(--red-bright)",
        digitGlow: "0 0 10px var(--red)",
      };
    case "empty":
    default:
      return {
        rim: "rgba(196,154,70,0.18)",
        glow:
          "inset 0 1px 0 rgba(255,255,255,0.025), inset 0 -1px 0 rgba(0,0,0,0.4)",
        faceTop: "#11151F",
        faceBottom: "#070A11",
        digitColor: "var(--fg-4)",
        digitGlow: "none",
      };
  }
}

export function CombinationSlot({
  digit = null,
  state = "empty",
  size = "md",
  index = 0,
}: {
  digit?: number | null;
  state?: SlotState;
  size?: SlotSize;
  index?: number;
}) {
  const dims = SLOT_DIMS[size];
  const s = styleForState(state);

  const wrapStyle: CSSProperties = {
    position: "relative",
    width: dims.w,
    height: dims.h,
    background: `linear-gradient(180deg, ${s.faceTop} 0%, ${s.faceBottom} 100%)`,
    border: `1px solid ${s.rim}`,
    borderRadius: 3,
    boxShadow: s.glow,
    display: "grid",
    placeItems: "center",
    overflow: "hidden",
    transition:
      "border-color 220ms cubic-bezier(.7,0,.2,1), box-shadow 220ms cubic-bezier(.7,0,.2,1)",
    flexShrink: 0,
  };

  return (
    <div style={wrapStyle}>
      {/* Tumbler-seam highlight (mid-line) */}
      <div
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          top: "50%",
          height: 1,
          background:
            "linear-gradient(90deg, transparent, rgba(196,154,70,0.4), transparent)",
          opacity: state === "empty" ? 0.3 : 0.7,
        }}
      />
      {/* Mount-pin notches */}
      <div
        style={{
          position: "absolute",
          top: 4,
          left: "50%",
          transform: "translateX(-50%)",
          width: dims.w * 0.18,
          height: 2,
          background: state === "empty" ? "rgba(196,154,70,0.2)" : "var(--gold-deep)",
          borderRadius: 1,
        }}
      />
      <div
        style={{
          position: "absolute",
          bottom: 4,
          left: "50%",
          transform: "translateX(-50%)",
          width: dims.w * 0.18,
          height: 2,
          background: state === "empty" ? "rgba(196,154,70,0.2)" : "var(--gold-deep)",
          borderRadius: 1,
        }}
      />
      {/* Digit */}
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontSize: dims.f,
          lineHeight: 1,
          color: s.digitColor,
          letterSpacing: -2,
          textShadow: s.digitGlow,
          userSelect: "none",
        }}
      >
        {digit !== null && digit !== undefined ? (
          digit
        ) : (
          <span style={{ opacity: 0.6 }}>—</span>
        )}
      </div>
      {/* Index hint */}
      <div
        style={{
          position: "absolute",
          left: 4,
          bottom: 3,
          fontFamily: "var(--font-mono)",
          fontSize: 8,
          color: "var(--fg-4)",
          letterSpacing: 1,
          opacity: 0.7,
        }}
      >
        0{index + 1}
      </div>
    </div>
  );
}

// ---- SlotRow ------------------------------------------------------------
// 4 slots side by side with separators.

export type SlotRowMode = "input" | "history" | "revealed-correct" | "revealed-wrong";

export function SlotRow({
  digits = [null, null, null, null],
  activeIndex = 0,
  mode = "input",
  size = "md",
}: {
  digits?: ReadonlyArray<number | null>;
  activeIndex?: number;
  mode?: SlotRowMode;
  size?: SlotSize;
}) {
  const sepHeight = size === "lg" ? 12 : size === "md" ? 9 : 6;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {[0, 1, 2, 3].map((i) => {
        const d = digits[i] ?? null;
        let state: SlotState;
        if (mode === "revealed-correct") state = "revealed-correct";
        else if (mode === "revealed-wrong") state = "revealed-wrong";
        else if (d === null || d === undefined) state = i === activeIndex ? "active" : "empty";
        else state = "filled";
        return (
          <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            <CombinationSlot digit={d} state={state} size={size} index={i} />
            {i < 3 && (
              <div
                style={{
                  width: 6,
                  height: sepHeight,
                  background: "var(--gold-deep)",
                  borderRadius: 1,
                  boxShadow: "0 0 4px rgba(196,154,70,0.3)",
                  opacity: 0.7,
                }}
              />
            )}
          </span>
        );
      })}
    </div>
  );
}

// ---- Peg ----------------------------------------------------------------
// Single feedback peg. Three kinds: gold (right pos), silver (right digit
// wrong pos), miss (not in code).

export function PegDot({ kind = "miss", size = 14 }: { kind?: Peg; size?: number }) {
  const cfg =
    kind === "gold"
      ? {
          bg:
            "radial-gradient(circle at 30% 30%, #FFE8A8, var(--gold) 60%, var(--gold-deep))",
          ring: "var(--gold-deep)",
          glow: "0 0 8px rgba(241,203,107,0.55)",
          borderWidth: 1,
        }
      : kind === "silver"
        ? {
            bg: "transparent",
            ring: "rgba(220,225,235,0.85)",
            glow: "inset 0 0 0 1.5px rgba(220,225,235,0.85)",
            borderWidth: 1.5,
          }
        : {
            bg: "var(--bg-2)",
            ring: "var(--line)",
            glow: "none",
            borderWidth: 1,
          };
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        background: cfg.bg,
        border: `${cfg.borderWidth}px solid ${cfg.ring}`,
        boxShadow: cfg.glow,
        flexShrink: 0,
      }}
    />
  );
}

// ---- PegCluster ---------------------------------------------------------
// 4 pegs in row or 2x2 grid layout. Default `sort = false`: pegs render in
// slot order (peg N = feedback for slot N), matching player intuition. Pass
// `sort={true}` for classic Mastermind unordered display (severity-sorted
// gold→silver→miss).

export function PegCluster({
  pegs = ["miss", "miss", "miss", "miss"],
  layout = "row",
  size = 14,
  sort = false,
}: {
  pegs?: ReadonlyArray<Peg>;
  layout?: "row" | "grid";
  size?: number;
  sort?: boolean;
}) {
  const order: Record<Peg, number> = { gold: 0, silver: 1, miss: 2 };
  const sorted = sort ? [...pegs].sort((a, b) => order[a] - order[b]) : [...pegs];

  if (layout === "grid") {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(2, ${size}px)`,
          gridTemplateRows: `repeat(2, ${size}px)`,
          gap: 4,
        }}
      >
        {sorted.map((p, i) => (
          <PegDot key={i} kind={p} size={size} />
        ))}
      </div>
    );
  }
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      {sorted.map((p, i) => (
        <PegDot key={i} kind={p} size={size} />
      ))}
    </div>
  );
}
